import React, { createContext, useContext, useState, useEffect } from "react";
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup
} from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp, addDoc, collection } from "firebase/firestore";
import { auth, db } from "./firebase";
import { logAuthAction, AUDIT_ACTIONS } from "./auditLogger";

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile from Firestore
  const fetchUserProfile = async (user) => {
    if (!user) {
      setUserProfile(null);
      return null;
    }
    
    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const profileData = userDoc.data();
        
        // Check if user has organizationId - if not, create one for existing users
        if (!profileData.organizationId) {
          console.log("User missing organizationId - creating organization...");
          
          // Create organization for existing user
          const displayName = profileData.displayName || user.displayName || user.email;
          const orgRef = await addDoc(collection(db, "organizations"), {
            name: `${displayName}'s Organization`,
            ownerId: user.uid,
            members: [user.uid],
            createdAt: serverTimestamp(),
            subscription: profileData.subscription || "free",
          });
          
          // Update user profile with organizationId
          await setDoc(doc(db, "users", user.uid), {
            ...profileData,
            organizationId: orgRef.id,
            role: profileData.role || "admin", // Ensure role is set
            updatedAt: serverTimestamp(),
          }, { merge: true });
          
          // Fetch updated profile
          const updatedDoc = await getDoc(doc(db, "users", user.uid));
          const profile = { id: updatedDoc.id, ...updatedDoc.data() };
          setUserProfile(profile);
          return profile;
        }
        
        const profile = { id: userDoc.id, ...profileData };
        setUserProfile(profile);
        return profile;
      }
      return null;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return null;
    }
  };

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchUserProfile(user);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Register new user
  const register = async (email, password, displayName, role = "viewer") => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update display name in Firebase Auth
      await updateProfile(user, { displayName });
      
      // Create organization for new user
      const orgRef = await addDoc(collection(db, "organizations"), {
        name: `${displayName}'s Organization`,
        ownerId: user.uid,
        members: [user.uid],
        createdAt: serverTimestamp(),
        subscription: "free",
      });
      
      // Create user profile in Firestore with organizationId
      await setDoc(doc(db, "users", user.uid), {
        email,
        displayName,
        role: "admin", // First user in org is admin
        organizationId: orgRef.id, // Link user to their organization
        teamId: null,
        subscription: "free",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
      });
      
      await fetchUserProfile(user);
      
      // Log registration event
      await logAuthAction(AUDIT_ACTIONS.REGISTER, {
        uid: user.uid,
        email: user.email,
        displayName: displayName,
        organizationId: orgRef.id
      }, "Email/Password registration");
      
      return { success: true, user };
    } catch (error) {
      console.error("Registration error:", error);
      return { success: false, error: error.message };
    }
  };

  // Login
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      await fetchUserProfile(userCredential.user);
      
      // Log login event
      await logAuthAction(AUDIT_ACTIONS.LOGIN, {
        uid: userCredential.user.uid,
        email: userCredential.user.email,
        displayName: userCredential.user.displayName || email
      }, "Email/Password login");
      
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error("Login error:", error);
      return { success: false, error: error.message };
    }
  };

  // Google Sign-In
  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // Check if user profile exists in Firestore
      const userDoc = await getDoc(doc(db, "users", user.uid));
      
      const isNewUser = !userDoc.exists();
      
      if (isNewUser) {
        // Create organization for new Google user
        const displayName = user.displayName || (user.email ? user.email.split('@')[0] : 'Google User');
        
        const orgRef = await addDoc(collection(db, "organizations"), {
          name: `${displayName}'s Organization`,
          ownerId: user.uid,
          members: [user.uid],
          createdAt: serverTimestamp(),
          subscription: "free",
        });
        
        // Create new user profile for Google sign-in users
        await setDoc(doc(db, "users", user.uid), {
          email: user.email,
          displayName,
          role: "admin", // First user in org is admin
          organizationId: orgRef.id, // Link user to their organization
          teamId: null,
          subscription: "free",
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isActive: true,
          authProvider: "google",
        });
        
        // Log registration
        await logAuthAction(AUDIT_ACTIONS.REGISTER, {
          uid: user.uid,
          email: user.email,
          displayName: displayName,
          organizationId: orgRef.id
        }, "Google Sign-In registration");
      }
      
      await fetchUserProfile(user);
      
      // Log login event
      await logAuthAction(AUDIT_ACTIONS.LOGIN, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || user.email
      }, "Google Sign-In");
      
      return { success: true, user };
    } catch (error) {
      console.error("Google Sign-In error:", error);
      return { success: false, error: error.message };
    }
  };

  // Logout
  const logout = async () => {
    try {
      // Log logout event before signing out
      if (currentUser) {
        await logAuthAction(AUDIT_ACTIONS.LOGOUT, {
          uid: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName || currentUser.email
        }, "User logged out");
      }
      
      await signOut(auth);
      setUserProfile(null);
      return { success: true };
    } catch (error) {
      console.error("Logout error:", error);
      return { success: false, error: error.message };
    }
  };

  // Update user profile
  const updateUserProfile = async (updates) => {
    if (!currentUser) return { success: false, error: "No user logged in" };
    
    try {
      const userRef = doc(db, "users", currentUser.uid);
      await setDoc(userRef, { ...updates, updatedAt: serverTimestamp() }, { merge: true });
      await fetchUserProfile(currentUser);
      return { success: true };
    } catch (error) {
      console.error("Update profile error:", error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    currentUser,
    userProfile,
    loading,
    register,
    login,
    signInWithGoogle,
    logout,
    updateUserProfile,
    fetchUserProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
